// frontend/src/components/BookReading/ChapterReader.js
//#region Imports
import React, { useEffect, useState, useContext, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from 'react-router-dom';
import { EditorState, convertFromRaw } from "draft-js";
import axios from "axios";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "draft-js/dist/Draft.css";
import { AuthContext } from "../../contexts/AuthContext";
import { useAlertContext } from '../../contexts/AlertContext';
import {
  Card,
  Alert,
  Stack,
  Button,
  Spinner,
} from "react-bootstrap";
import { API_ENDPOINTS } from "../../configs/apiConfig";
import { getChapterAccessFromLocationData } from '../../utils/locationUtils';
import { BookReadingContext } from './BookReading';
import { markChapterRead } from './readingProgress';
import ChapterPreview from "../ChapterTextEditor/ChapterPreview";
import '../../styles/App.css';
import { RiArrowUpBoxLine, RiChatDownloadLine } from "react-icons/ri";
import { FaChevronLeft, FaChevronRight, FaCheck } from "react-icons/fa";

// Importar componentes e utilitários desmembrados
import ProductDetailsModal from "./ProductDetailsModal";
import { PesticideTable, FertilizerTable } from "./ProductTables";
import ContributorsTable from "./ContributorsTable";
import SuggestionForm from "./SuggestionForm";
import PaginationComponent from "../PaginationComponent";
import useMediaQuery from "../../utils/useMediaQuery"; // Import the custom hook
//#endregion

//#region Componente Principal
function ChapterReader() {
  //#region Declarações
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const { username } = useContext(AuthContext);
  const {
    selectedChapter,
    setSelectedChapter,
    selectedBook,
    setSelectedBook,
    suggestionTypes,
    loadingSuggestionTypes,
    translations,
    backToChapters
  } = useContext(BookReadingContext);

  const readerContainerRef = useRef(null);
  const readerInternRef = useRef(null);
  const suggestionFormRef = useRef(null);

  const { showSuccess, showWarning, showError } = useAlertContext();

  const [loading, setLoading] = useState(true);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [suggestion, setSuggestion] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedSuggestionType, setSelectedSuggestionType] = useState("");
  const [showProductTable, setShowProductTable] = useState(false);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productsLoaded, setProductsLoaded] = useState(false);
  const [showContributorsTable, setShowContributorsTable] = useState(false);
  const [contributors, setContributors] = useState([]);
  const [loadingContributors, setLoadingContributors] = useState(false);
  const [contributorsLoaded, setContributorsLoaded] = useState(false);
  const [productsPerPage] = useState(10);
  const [currentProductPage, setCurrentProductPage] = useState(1);
  const [contributorsPerPage] = useState(10);
  const [currentContributorsPage, setCurrentContributorsPage] = useState(1);
  const textareaRef = useRef(null);
  const [totalPages, setTotalPages] = useState(1);
  const [previewReady, setPreviewReady] = useState(false);
  const [showProductDetails, setShowProductDetails] = useState(false);
  const [shouldForceShowProducts, setShouldForceShowProducts] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Constante para detectar dispositivos móveis
  const isMobile = useMediaQuery("(max-width: 768px)");

  const memoizedChapterPreview = useMemo(() => {
    if (loading || shouldForceShowProducts || showProductTable) return null;

    return (
      <div ref={readerContainerRef}>
        <ChapterPreview
          refs={readerInternRef}
          editorState={editorState}
          currentPage={1}
          totalPages={totalPages}
          setTotalPages={setTotalPages}
          setCurrentPage={setCurrentPage}
          t={translations}
        />
      </div>
    );
  }, [editorState, totalPages, translations, loading, shouldForceShowProducts, showProductTable]);
  //#endregion

  //#region Funções Auxiliares
  // Manipular exibição de detalhes do produto
  const handleViewProductDetails = (product) => {
    setSelectedProduct(product);
    setShowProductDetails(true);
  };

  // Alternar entre visualização de produtos, contribuidores e leitor
  const handleShowProducts = () => {
    setShowProductTable(true);
    setShowContributorsTable(false);
    // fetchProducts();
  };

  const handleShowContributors = () => {
    setShowContributorsTable(true);
    setShowProductTable(false);
    // fetchContributors();
  };

  const handleBackToReader = () => {
    setShowProductTable(false);
    setShowContributorsTable(false);
  };

  const scrollToSuggestionForm = () => {
    suggestionFormRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });

  };

  const scrollToTop = () => {
    const valueTop = readerContainerRef.current?.getBoundingClientRect().top || 0;
    if (valueTop > -5 && valueTop < 60) {
      readerInternRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
    readerContainerRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  //#endregion

  //#region Carregamento de Dados


  // Carregar produtos associados ao capítulo
  const fetchProducts = useCallback(async () => {
    if (!selectedChapter) return;

    setLoadingProducts(true);
    setProductsLoaded(false);

    try {
      const auth = localStorage.getItem("auth");
      const endpoint = API_ENDPOINTS.CHAPTER_PRODUCTS(selectedChapter.id);
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Basic ${auth}` },
      });
      setProducts(response.data || []);
      setProductsLoaded(true);
      setLoadingProducts(false);
    } catch (error) {
      console.error("Erro ao buscar produtos:", error);
      showError(translations.failProducts + (error.message || "Erro desconhecido"));
      setProducts([]);
      setProductsLoaded(true);
      setLoadingProducts(false);
    }
  }, [selectedChapter, translations.failProducts, showError]);

  useEffect(() => {
    if (previewReady) {
      setPreviewReady(false);
    }
  }, [previewReady]);
  // Carregar contribuidores associados ao capítulo
  const fetchContributors = useCallback(async () => {
    if (!selectedChapter) return;

    setLoadingContributors(true);
    setContributorsLoaded(false);

    try {
      const auth = localStorage.getItem("auth");
      const endpoint = API_ENDPOINTS.CHAPTER_CONTRIBUITIONS(selectedChapter.id);
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Basic ${auth}` },
      });
      setContributors(response.data || []);
    } catch (error) {
      console.error("Erro ao buscar contribuidores:", error);
      showError(translations.failContributors + (error.message || "Erro desconhecido"));
      setContributors([]);
    } finally {
      setContributorsLoaded(true);
      setLoadingContributors(false);
    }
  }, [selectedChapter, translations.failContributors, showError]);




  // Carregar dados do capítulo quando o componente monta
  useEffect(() => {
    const auth = localStorage.getItem("auth");

    const loadChapterData = async () => {
      setPreviewReady(true);

      try {
        const response = await axios.get(API_ENDPOINTS.CHAPTERS_BY_ID(chapterId), {
          headers: { Authorization: `Basic ${auth}` }
        });

        const chapter = response.data;
        setSelectedChapter(chapter);

        // "Continuar de onde parou" — guarda o último capítulo lido (por usuário)
        if (username) {
          try {
            localStorage.setItem(`lastRead_${username}`, JSON.stringify({
              chapterId,
              chapterTitle: chapter.title,
              bookId: chapter.book?.id,
              bookTitle: chapter.book?.title,
              at: Date.now(),
            }));
          } catch (e) { /* ignora */ }
          // Marca o capítulo como lido (progresso do livro)
          markChapterRead(username, chapterId);
        }

        // Se não temos o livro carregado, carregar também
        if (!selectedBook && chapter.book) {
          setSelectedBook(chapter.book);
        }

        // NOVO: Definir o estado inicial de shouldForceShowProducts com base na propriedade onlyProducts
        if (chapter.onlyProducts === true) {
          setShouldForceShowProducts(true);
          setShowProductTable(true); // Forçar a exibição da tabela de produtos
        }

        setLoading(false);
        // Registrar acesso ao capítulo
      } catch (error) {
        console.error('Erro ao carregar capítulo:', error);
        showError(translations.failChapterAccess + error.message);
        navigate('..');
      } finally {
        setLoading(false);
      }
    };

    if (chapterId) {
      loadChapterData();
      registerChapterAccess(chapterId, username, auth);
    }
  }, [chapterId, setSelectedChapter, setSelectedBook, selectedBook, showError, navigate, translations, username]);

  useEffect(() => {
    if (selectedChapter) {
      fetchProducts();
      fetchContributors();
    }
  }, [selectedChapter, fetchProducts, fetchContributors]);

  // Processar conteúdo do capítulo quando carregado
  useEffect(() => {
    if (selectedChapter && selectedChapter.content) {
      try {
        const parsedContent = JSON.parse(selectedChapter.content);
        const contentState = convertFromRaw(parsedContent);
        setEditorState(EditorState.createWithContent(contentState));
        setCurrentPage(1);
      } catch (error) {
        console.error("Erro ao carregar conteúdo:", error);
        showError(translations.failBooks + error.message);
        setEditorState(EditorState.createEmpty());
      }
    } else {
      setEditorState(EditorState.createEmpty());
      setCurrentPage(1);
    }
    setPreviewReady(false);
  }, [selectedChapter, translations.failBooks, showError]);
  //#endregion

  //#region Manipulação de Sugestões
  // Enviar sugestão para o capítulo
  const handleSuggestionSubmit = async (formData) => {
    setLoadingSuggestions(true);
    // Extrair os dados do FormData
    const suggestionText = formData.get("suggestion");
    const suggestionType = formData.get("suggestionType");
    const imageBase64 = formData.get("imageBase64"); // Este será o seu arquivo em base64
    const nameImageBase64 = formData.get("nameImageBase64");

    if (!suggestionText || !suggestionText.trim()) {
      showWarning(translations.suggestionEmpty);
      return;
    }

    try {
      const auth = localStorage.getItem("auth");

      // Construir o objeto de dados a ser enviado, incluindo imageBase64 se existir
      const dataToSend = {
        text: suggestionText,
        chapter: { id: selectedChapter.id },
        pageNumber: currentPage,
        sugestionType: suggestionType,
        nameImageBase64: nameImageBase64
      };

      if (imageBase64) {
        dataToSend.imageBase64 = imageBase64;
      }

      await axios.post(
        API_ENDPOINTS.COMMENTS,
        dataToSend, // Enviar o objeto construído
        {
          headers: {
            Authorization: `Basic ${auth}`,
            "Content-Type": "application/json", // Manter application/json
          },
        }
      );
      setSuggestion("");
      setSelectedSuggestionType("");
      showSuccess(translations.suggestionSuccess);
    } catch (error) {
      showError(translations.suggestionFail + error.message);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  //#endregion

  //#region Manipulação de Paginação
  // Calcular páginas de produtos e contribuidores
  const pesticides = products.filter((product) => product.type === "PESTICIDE");
  const fertilizers = products.filter((product) => product.type === "FERTILIZER");
  const indexOfLastProduct = currentProductPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentPesticides = pesticides.slice(indexOfFirstProduct, indexOfLastProduct);
  const currentFertilizers = fertilizers.slice(indexOfFirstProduct, indexOfLastProduct);

  const totalPesticidePages = Math.ceil(pesticides.length / productsPerPage);
  const totalFertilizerPages = Math.ceil(fertilizers.length / productsPerPage);
  const totalProductPages = Math.max(totalPesticidePages, totalFertilizerPages);

  const indexOfLastContributor = currentContributorsPage * contributorsPerPage;
  const indexOfFirstContributor = indexOfLastContributor - contributorsPerPage;
  const currentContributors = contributors.slice(indexOfFirstContributor, indexOfLastContributor);
  const totalContributorsPages = Math.ceil(contributors.length / contributorsPerPage);
  //#endregion

  //#region Renderização


  // Se a condição for atendida, forçamos showProductTable para true
  const isProductTableVisible = showProductTable || shouldForceShowProducts;

  // Navegação entre capítulos (anterior / próximo) dentro do livro
  const orderedChapters = (selectedBook?.chapters || [])
    .filter((c) => c.visible !== false)
    .slice()
    .sort((a, b) => a.position - b.position);
  const currentIndex = orderedChapters.findIndex(
    (c) => Number(c.id) === Number(selectedChapter?.id)
  );
  const prevChapter = currentIndex > 0 ? orderedChapters[currentIndex - 1] : null;
  const nextChapter =
    currentIndex >= 0 && currentIndex < orderedChapters.length - 1
      ? orderedChapters[currentIndex + 1]
      : null;

  const goToChapter = (ch) => {
    if (!ch) return;
    setSelectedChapter(ch);
    navigate(`../chapter/${ch.id}`);
  };

  const handleCompleteAndNext = () => {
    markChapterRead(username, selectedChapter.id);
    if (nextChapter) {
      goToChapter(nextChapter);
    } else {
      backToChapters();
    }
  };

  if (previewReady) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{translations.loading}</span>
        </Spinner>
        <p className="mt-3">{translations.loadingChapter}</p>
      </div>
    );
  }

  if (!selectedChapter) {
    return (
      <Alert variant="warning">
        <Alert.Heading>{translations.chapterNotFound}</Alert.Heading>
        <p>{translations.chapterNotFoundDescription}</p>
        <Button variant="outline-warning" onClick={() => navigate('..')}>
          {translations.backToChapters}
        </Button>
      </Alert>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-4">
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <Button variant="link" className="p-0" onClick={() => navigate('../..')}>
              {translations.books}
            </Button>
          </li>
          {selectedBook && (
            <li className="breadcrumb-item">
              <Button variant="link" className="p-0" onClick={backToChapters}>
                {selectedBook.title}
              </Button>
            </li>
          )}
          <li className="breadcrumb-item active" aria-current="page">
            {selectedChapter.title}
          </li>
        </ol>
      </nav>

      <Card>
        <Card.Header>
          <Stack className="w-100 d-flex flex-column flex-md-row gap-3">
            <div>
              <h4 className="mb-0">{selectedChapter.title}</h4>
              {!isProductTableVisible && !showContributorsTable && (
                <small className="text-muted">
                  {totalPages} {translations.pages}
                </small>
              )}
            </div>
            <div className="ms-auto d-flex gap-2">
              {(isProductTableVisible || showContributorsTable) && !shouldForceShowProducts ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleBackToReader}
                >
                  {translations.backToReader}
                </Button>
              ) : (
                <>
                  {/* Botão de produtos - só aparece se houver produtos ou ainda estiver carregando */}
                  {(products.length > 0 || loadingProducts) && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleShowProducts}
                      disabled={loadingProducts}
                    >
                      {loadingProducts ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>{" "}
                          Loading...
                        </>
                      ) : (
                        `${translations.listProducts} (${products.length})`
                      )}
                    </Button>
                  )}

                  {/* Botão de contribuidores - só aparece se houver contribuidores ou ainda estiver carregando */}
                  {(contributors.length > 0 || loadingContributors) && (
                    <Button
                      variant="outline-info"
                      size="sm"
                      onClick={handleShowContributors}
                      disabled={loadingContributors}
                    >
                      {loadingContributors ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          ></span>{" "}
                          Loading...
                        </>
                      ) : (
                        `${translations.listContributors} (${contributors.length})`
                      )}
                    </Button>
                  )}
                </>
              )}
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={backToChapters}
              >
                {translations.backToChapters}
              </Button>
            </div>
          </Stack>
        </Card.Header>

        <Card.Body>
          {isProductTableVisible ? (
            <div>
              <h5 className="mb-4">{translations.productList}</h5>
              {loadingProducts ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{translations.loadingProducts}</span>
                  </div>
                  <p className="mt-3">{translations.loadingProducts}</p>
                </div>
              ) : productsLoaded ? (
                <>
                  <PesticideTable
                    products={currentPesticides}
                    title={translations.pesticides}
                    emptyMessage={translations.noPesticides}
                    t={translations}
                    handleViewProductDetails={handleViewProductDetails}
                  />
                  <FertilizerTable
                    products={currentFertilizers}
                    title={translations.fertilizers}
                    emptyMessage={translations.noFertilizers}
                    t={translations}
                    handleViewProductDetails={handleViewProductDetails}
                  />
                  <PaginationComponent
                    totalPages={totalProductPages}
                    isMobile={isMobile}
                    setCurrentPage={setCurrentProductPage}
                    currentPage={currentProductPage}
                    t={translations}
                    tItemName={translations.products}
                    indexOfFirst={0}
                    indexOfLast={0}
                    lengthTotal={products.length}
                  />
                </>
              ) : (
                <Alert variant="info">{translations.noProducts}</Alert>
              )}
            </div>
          ) : showContributorsTable ? (
            <div>
              {loadingContributors ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-info" role="status">
                    <span className="visually-hidden">{translations.loadingContributors}</span>
                  </div>
                  <p className="mt-3">{translations.loadingContributors}</p>
                </div>
              ) : contributorsLoaded ? (
                <ContributorsTable
                  totalContributors={contributors.length}
                  currentContributors={currentContributors}
                  emptyMessage={translations.noContributors}
                  t={translations}
                  indexOfFirstContributor={indexOfFirstContributor}
                  totalContributorsPages={totalContributorsPages}
                  currentContributorsPage={currentContributorsPage}
                  setCurrentContributorsPage={setCurrentContributorsPage}
                />
              ) : (
                <Alert variant="info">{translations.noContributors}</Alert>
              )}
            </div>
          ) : (
            <>
              {/* Renderiza o ChapterPreview APENAS se o conteúdo não estiver vazio e não for forçado a mostrar produtos */}
              {(!shouldForceShowProducts || !showProductTable) && (
                <>
                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{translations.loading}</span>
                      </div>
                      <p className="mt-3">{translations.loading}</p>
                    </div>
                  ) : (
                    memoizedChapterPreview
                  )}
                </>
              )}
              <div ref={suggestionFormRef}>
                <SuggestionForm
                  suggestion={suggestion}
                  setSuggestion={setSuggestion}
                  selectedSuggestionType={selectedSuggestionType}
                  setSelectedSuggestionType={setSelectedSuggestionType}
                  loadingSuggestionTypes={loadingSuggestionTypes}
                  suggestionTypes={suggestionTypes}
                  handleSuggestionSubmit={handleSuggestionSubmit}
                  translations={translations}
                  textareaRef={textareaRef}
                  loading={loadingSuggestions}
                />
              </div>

              {/* Navegação entre capítulos */}
              {orderedChapters.length > 1 && currentIndex >= 0 && (
                <div className="chapter-nav mt-4 pt-3">
                  <Button
                    variant="outline-secondary"
                    disabled={!prevChapter}
                    onClick={() => goToChapter(prevChapter)}
                  >
                    <FaChevronLeft className="me-2" />
                    {translations.previousChapter}
                  </Button>
                  <Button variant="brand" onClick={handleCompleteAndNext}>
                    {nextChapter ? (
                      <>
                        {translations.completeAndNext}
                        <FaChevronRight className="ms-2" />
                      </>
                    ) : (
                      <>
                        <FaCheck className="me-2" />
                        {translations.finishBook}
                      </>
                    )}
                  </Button>
                </div>
              )}
            </>
          )}
        </Card.Body>
      </Card>

      <ProductDetailsModal
        show={showProductDetails}
        onHide={() => setShowProductDetails(false)}
        selectedProduct={selectedProduct}
        translations={translations}
      />

      {/* Botão flutuante de scroll */}
      {isMobile && !loading && !isProductTableVisible && !showContributorsTable && (
        <>
          <Button
            variant="secondary"
            className="position-fixed shadow-lg p-0 m-0"
            style={{
              bottom: '5rem',
              right: '1rem',
              width: '3rem',
              height: '3rem',
              borderRadius: '5px 5px 0px 0px',
              borderBottom: '1px solid white',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={scrollToTop}
            title={translations.backToTop}
          >
            <RiArrowUpBoxLine size={33} />
          </Button>
          <Button
            variant="secondary"
            className="position-fixed shadow-lg p-0 m-0"
            style={{
              bottom: '2rem',
              right: '1rem',
              width: '3rem',
              height: '3rem',
              borderRadius: '0px 0px 5px 5px',
              borderTop: '1px solid white',
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            onClick={scrollToSuggestionForm}
            title={translations.addComment}
          >
            <RiChatDownloadLine size={30} style={{ paddingLeft: '1px' }} />
          </Button>
        </>

      )}
    </div>
  );
  //#endregion
}

export default ChapterReader;

async function registerChapterAccess(chapterId, username, auth) {
  try {
    const locationData = await getChapterAccessFromLocationData();
    const chapterAccessData = {
      ...locationData,
      chapterId: chapterId,
      visitorName: username
    };

    await axios.post(API_ENDPOINTS.CHAPTER_ACESS_REGISTER, chapterAccessData, {
      headers: { Authorization: `Basic ${auth}` }
    });
  } catch (error) {
    console.error("Erro ao registrar acesso ao capítulo:", error);
  }
}

